import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { NavDropdown, Nav } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCompanyStore, useEbooksStore, useUserStore } from 'stores';
//api
import { getEbooksSsoUrl } from 'api/publishit';
//custom-hook
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';
// components
import CustomDropdown from 'components/navbar/CustomDropdown';

const DESKTOP_VISIBLE_MENU_LIMIT = 8;

const MenuItems = ({ blogCategories, setShowOffCanvas, isDesktop }) => {
  const { t, i18n } = useTranslation(['header']);
  const { menus, is_single_company: isMonoCliente } = useCompanyStore();
  const { assignedLibrary, libraryPath } = useAssignedLibrary();
  const { expirationDate, setEbooksExpirationDate, isTokenExpired } = useEbooksStore();
  const { isLogged } = useUserStore();
  const [isLoadingEbooks, setIsLoadingEbooks] = useState(false);

  // Función helper para extraer la fecha de expiración del token JWT en la URL
  const extractExpirationFromToken = (url) => {
    try {
      // Extraer el token de la URL
      const urlObj = new URL(url);
      const token = urlObj.searchParams.get('external-auth-token');
      
      if (!token) return null;
      
      // El JWT tiene 3 partes separadas por puntos: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      // Decodificar el payload (segunda parte)
      // Base64 URL-safe decoding
      const payload = parts[1];
      // Reemplazar caracteres URL-safe de base64
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      // Agregar padding si es necesario
      const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
      
      // Decodificar
      const decodedPayload = JSON.parse(atob(padded));
      
      // Obtener el campo 'exp' (timestamp Unix en segundos)
      if (decodedPayload.exp) {
        // Convertir timestamp Unix a formato ISO
        const expirationDate = new Date(decodedPayload.exp * 1000);
        return expirationDate.toISOString();
      }
      
      return null;
    } catch (error) {
      console.error('Error al extraer fecha de expiración del token:', error);
      return null;
    }
  };

  const filteredMenus = Array.isArray(menus)
    ? menus.filter((menu) => {
        if (isMonoCliente && menu.url === '/librerias') return false;
        return true;
      })
    : [];

  const handleEbooksClick = async (e) => {
    e.preventDefault();
    if (setShowOffCanvas) setShowOffCanvas(false);
    
    // Si el usuario NO está logueado, redirigir directamente a la URL estática sin hacer petición
    if (!isLogged) {
      window.location.href = 'https://ebookslibelista.publica.la/library';
      return;
    }
    
    setIsLoadingEbooks(true);
    
    try {
      // Verificar si existe fecha de caducidad en el store
      if (expirationDate && !isTokenExpired()) {
        // Si el token NO ha expirado, redirigir directamente a la URL base sin llamar al endpoint
        window.location.href = 'https://ebookslibelista.publica.la/library';
        setIsLoadingEbooks(false);
        return;
      }
      
      // Si el token ha expirado o no existe, llamar al endpoint para obtener nueva URL con token
      const response = await getEbooksSsoUrl();
      if (response.status === 200 && response.data) {
        const { url, expires_at_iso } = response.data;
        if (url) {
          // Obtener fecha de expiración: primero intentar expires_at_iso, si no existe extraer del token
          let expirationDateToSave = expires_at_iso;
          
          if (!expirationDateToSave) {
            // Fallback: extraer la fecha de expiración del token JWT en la URL
            expirationDateToSave = extractExpirationFromToken(url);
          }
          
          // Guardar solo la fecha de caducidad en el store (no la URL porque contiene el token)
          if (expirationDateToSave) {
            setEbooksExpirationDate(expirationDateToSave);
          }
          // Redirigir inmediatamente a la URL obtenida
          window.location.href = url;
        } else {
          console.error('Error: No se recibió URL en la respuesta');
        }
      } else {
        console.error('Error al obtener la URL de SSO de ebooks');
      }
    } catch (error) {
      console.error('Error en la petición de SSO de ebooks:', error);
    } finally {
      setIsLoadingEbooks(false);
    }
  };

  const renderSubmenuItems = (submenu) => {
    if (!submenu || !Array.isArray(submenu) || submenu.length === 0) return null;
    
    return submenu.map((item) => {
      if (!item) return null;
      const { text, url, id, isExternal } = item;
      const localizedText = item[`text_${i18n.language}`] || text;
      const isObject = typeof url === 'object';
      let finalUrl = isObject ? `${url.pathname}${url.search}` : url || '';
      
      if (isExternal && finalUrl) {
        return (
          <NavDropdown.Item
            href={finalUrl}
            key={id}
            onClick={() => setShowOffCanvas && setShowOffCanvas(false)}
          >
            {t(localizedText).toLocaleUpperCase()}
          </NavDropdown.Item>
        );
      }
      
      if (finalUrl && !finalUrl.startsWith(`/libelista/`)) {
        finalUrl = libraryPath(finalUrl.replace(/^\//, ''));
      }
      
      return (
        <NavDropdown.Item
          as={Link}
          to={finalUrl}
          key={id}
          onClick={() => setShowOffCanvas && setShowOffCanvas(false)}
          eventKey={finalUrl}
          state={isObject ? url.state || {} : {}}
        >
          {t(localizedText).toLocaleUpperCase()}
        </NavDropdown.Item>
      );
    });
  };

  const getLocalizedMenuText = (menu) => {
    const translatedText = menu[`text_${i18n.language}`] || menu.text;
    return t(translatedText).toLocaleUpperCase();
  };

  const getMenuUrl = (menu) => {
    const isObject = typeof menu.url === 'object';
    let finalUrl = isObject ? `${menu.url.pathname}${menu.url.search}` : menu.url || '';

    if (menu.keyword === '#gallery') finalUrl = 'gallery';
    if (menu.keyword === '#infantil') finalUrl = 'para-infantil';

    if (finalUrl && !menu.isExternal && !finalUrl.startsWith(`/libelista/`)) {
      finalUrl = libraryPath(finalUrl.replace(/^\//, ''));
    }

    return finalUrl;
  };

  const renderOverflowMenuItem = (menu) => {
    const { id, keyword, submenu, url } = menu;
    const hasSubmenu = submenu && Array.isArray(submenu) && submenu.length > 0;
    const text = getLocalizedMenuText(menu);

    if (keyword === '#ebooks') {
      return (
        <NavDropdown.Item href="#" onClick={handleEbooksClick} key={id} disabled={isLoadingEbooks}>
          {isLoadingEbooks ? t('loading') + '...' : text}
        </NavDropdown.Item>
      );
    }

    if (keyword === '#blog') {
      return [
        <NavDropdown.Header key={`${id}-header`}>{text}</NavDropdown.Header>,
        hasSubmenu && <React.Fragment key={`${id}-submenu`}>{renderSubmenuItems(submenu)}</React.Fragment>,
        blogCategories?.length > 0 &&
          blogCategories.map((cat) => (
            <NavDropdown.Item
              as={Link}
              to={{ pathname: libraryPath(`blog/${cat.id}/${cat.slug}/`) }}
              key={`${id}-blog-${cat.id}`}
              eventKey={`/blog/${cat.id}/${cat.slug}/`}
            >
              {cat.name}
            </NavDropdown.Item>
          )),
      ];
    }

    if (hasSubmenu) {
      return [
        <NavDropdown.Header key={`${id}-header`}>{text}</NavDropdown.Header>,
        <React.Fragment key={`${id}-submenu`}>{renderSubmenuItems(submenu)}</React.Fragment>,
      ];
    }

    const finalUrl = getMenuUrl(menu);
    const isObject = typeof url === 'object';

    if (menu.isExternal && finalUrl) {
      return (
        <NavDropdown.Item href={finalUrl} key={id}>
          {text}
        </NavDropdown.Item>
      );
    }

    return (
      <NavDropdown.Item
        as={Link}
        to={finalUrl}
        key={id}
        eventKey={finalUrl}
        state={isObject ? url.state || {} : {}}
      >
        {text}
      </NavDropdown.Item>
    );
  };

  const renderMenuItem = (menu) => {
    const { text, url, id, keyword, submenu } = menu;
    const translatedText = menu[`text_${i18n.language}`] || text;
    const hasSubmenu = submenu && Array.isArray(submenu) && submenu.length > 0;
    switch (keyword) {
      case '#books':
        return <CustomDropdown setShowOffCanvas={setShowOffCanvas} isDesktop={isDesktop} menu={menu} key={id} />;
      case '#ebooks':
        return (
          <Nav.Link
            href="#"
            onClick={handleEbooksClick}
            className={setShowOffCanvas && 'navbar-item-border'}
            key={id}
            disabled={isLoadingEbooks}
          >
            {isLoadingEbooks ? t('loading') + '...' : t(translatedText).toLocaleUpperCase()}
          </Nav.Link>
        );
      case '#blog':
        return (
          <NavDropdown
            title={t(text).toLocaleUpperCase()}
            className={setShowOffCanvas ? 'navbar-item-border' : 'dropdown-lipareso'}
            key={id}
          >
            {hasSubmenu && renderSubmenuItems(submenu)}
            {blogCategories?.length > 0 &&
              blogCategories?.map((cat) => {
                return (
                  <NavDropdown.Item
                    as={Link}
                    to={{ pathname: libraryPath(`blog/${cat.id}/${cat.slug}/`) }}
                    key={cat.id}
                    onClick={() => setShowOffCanvas && setShowOffCanvas(false)}
                    eventKey={`/blog/${cat.id}/${cat.slug}/`}
                  >
                    {cat.name}
                  </NavDropdown.Item>
                );
              })}
          </NavDropdown>
        );
      case '#gallery':
        const urlGallery = libraryPath('gallery');
        if (hasSubmenu) {
          return (
            <NavDropdown
              title={t(translatedText).toLocaleUpperCase()}
              className={setShowOffCanvas ? 'navbar-item-border' : 'dropdown-lipareso'}
              key={id}
            >
              {renderSubmenuItems(submenu)}
            </NavDropdown>
          );
        }
        return (
          <Nav.Link
            as={Link}
            to={urlGallery}
            onClick={() => setShowOffCanvas && setShowOffCanvas(false)}
            className={setShowOffCanvas && 'navbar-item-border'}
            eventKey={urlGallery}
            key={id}
          >
            {t(translatedText).toLocaleUpperCase()}
          </Nav.Link>
        );
      case '#infantil':
        const urlChildren = libraryPath('para-infantil');
        if (hasSubmenu) {
          return (
            <NavDropdown
              title={t(translatedText).toLocaleUpperCase()}
              className={setShowOffCanvas ? 'navbar-item-border' : 'dropdown-lipareso'}
              key={id}
            >
              {renderSubmenuItems(submenu)}
            </NavDropdown>
          );
        }
        return (
          <Nav.Link
            as={Link}
            to={urlChildren}
            onClick={() => setShowOffCanvas && setShowOffCanvas(false)}
            className={setShowOffCanvas && 'navbar-item-border'}
            eventKey={urlChildren}
            key={id}
          >
            {t(translatedText).toLocaleUpperCase()}
          </Nav.Link>
        );
      default:
        const isObject = typeof url === 'object';
        let finalUrl = isObject ? `${url.pathname}${url.search}` : url || '';
        
        if (hasSubmenu) {
          return (
            <NavDropdown
              title={t(translatedText).toLocaleUpperCase()}
              className={setShowOffCanvas ? 'navbar-item-border' : 'dropdown-lipareso'}
              key={id}
            >
              {renderSubmenuItems(submenu)}
            </NavDropdown>
          );
        }
        
        if (menu.isExternal && finalUrl) {
          return (
            <Nav.Link
              href={finalUrl}
              onClick={() => setShowOffCanvas && setShowOffCanvas(false)}
              className={setShowOffCanvas && 'navbar-item-border'}
              key={id}
            >
              {t(translatedText).toLocaleUpperCase()}
            </Nav.Link>
          );
        }
        
        if (finalUrl && !finalUrl.startsWith(`/libelista/`)) {
          finalUrl = libraryPath(finalUrl.replace(/^\//, ''));
        }
        return (
          <Nav.Link
            as={Link}
            to={finalUrl}
            onClick={() => setShowOffCanvas && setShowOffCanvas(false)}
            className={setShowOffCanvas && 'navbar-item-border'}
            eventKey={finalUrl}
            key={id}
            state={isObject ? url.state || {} : {}}
          >
            {t(translatedText).toLocaleUpperCase()}
          </Nav.Link>
        );
    }
  };

  if (!isDesktop) return filteredMenus.map(renderMenuItem);

  const visibleMenus = filteredMenus.slice(0, DESKTOP_VISIBLE_MENU_LIMIT);
  const overflowMenus = filteredMenus.slice(DESKTOP_VISIBLE_MENU_LIMIT);

  return [
    ...visibleMenus.map(renderMenuItem),
    overflowMenus.length > 0 && (
      <NavDropdown title={t('moreMenu').toLocaleUpperCase()} className="dropdown-lipareso" key="menu-more">
        {overflowMenus.map(renderOverflowMenuItem)}
      </NavDropdown>
    ),
  ];
};

MenuItems.propTypes = {
  isDesktop: PropTypes.bool,
  blogCategories: PropTypes.any,
  setShowOffCanvas: PropTypes.func,
};

export default MenuItems;
